import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { API_URL } from '@/config';

interface User {
  id: string;
  email: string;
  username?: string;
  full_name?: string;
  role: string;
  organization_id: string;
  is_active: boolean;
  avatar_url?: string;
  github_id?: string;
  github_connected?: boolean;
  created_at: string;
  updated_at: string;
}

interface RegistrationData {
  email: string;
  password: string;
  username: string;
  full_name?: string;
  organization_name?: string;
}

interface AuthTokens {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
}

interface AuthState {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  tokens: AuthTokens | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (registrationData: RegistrationData) => Promise<void>;
  loginWithGitHub: () => void;
  logout: () => void;
  refreshAccessToken: () => Promise<void>;
  setTokens: (tokens: AuthTokens) => void;
  clearAuth: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      refreshToken: null,
      tokens: null,
      isAuthenticated: false,

      login: async (email: string, password: string) => {
        const response = await fetch(`${API_URL}/api/v1/auth/login`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email, password }),
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.detail || 'Login failed');
        }

        const data = await response.json();
        
        const tokens = {
          access_token: data.access_token,
          refresh_token: data.refresh_token,
          token_type: data.token_type || 'bearer',
          expires_in: data.expires_in || 3600,
        };
        
        set({
          user: data.user,
          token: data.access_token,
          refreshToken: data.refresh_token,
          tokens,
          isAuthenticated: true,
        });
      },

      register: async (registrationData: RegistrationData) => {
        const response = await fetch(`${API_URL}/api/v1/auth/register`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(registrationData),
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.detail || 'Registration failed');
        }

        const data = await response.json();

        const tokens = {
          access_token: data.access_token,
          refresh_token: data.refresh_token,
          token_type: data.token_type || 'bearer',
          expires_in: data.expires_in || 3600,
        };

        set({
          user: data.user,
          token: data.access_token,
          refreshToken: data.refresh_token,
          tokens,
          isAuthenticated: true,
        });
      },

      loginWithGitHub: () => {
        window.location.href = `${API_URL}/api/v1/auth/github`;
      },

      logout: async () => {
        const { token } = get();
        
        if (token) {
          try {
            await fetch(`${API_URL}/api/v1/auth/logout`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
              },
            });
          } catch (error) {
            console.error('Logout error:', error);
          }
        }

        set({
          user: null,
          token: null,
          refreshToken: null,
          tokens: null,
          isAuthenticated: false,
        });
      },

      refreshAccessToken: async () => {
        const { refreshToken } = get();
        
        if (!refreshToken) {
          throw new Error('No refresh token available');
        }

        const response = await fetch(`${API_URL}/api/v1/auth/refresh`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ refresh_token: refreshToken }),
        });

        if (!response.ok) {
          // Refresh token is invalid, logout user
          get().logout();
          throw new Error('Session expired');
        }

        const data = await response.json();
        
        const tokens = {
          access_token: data.access_token,
          refresh_token: data.refresh_token,
          token_type: data.token_type || 'bearer',
          expires_in: data.expires_in || 3600,
        };
        
        set({
          user: data.user,
          token: data.access_token,
          refreshToken: data.refresh_token,
          tokens,
        });
      },

      setTokens: (newTokens: AuthTokens) => {
        set({
          token: newTokens.access_token,
          refreshToken: newTokens.refresh_token,
          tokens: newTokens,
        });
      },

      clearAuth: () => {
        set({
          user: null,
          token: null,
          refreshToken: null,
          tokens: null,
          isAuthenticated: false,
        });
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        refreshToken: state.refreshToken,
        tokens: state.tokens,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
